import React, { FunctionComponent, useState } from 'react';

import { forNamespace, forTemplate } from 'app/common/lib/util/i18n';
import { PrimaryButton } from 'app/common/components/controls/button/button';
import { OnboardingDialog } from './OnboardingDialog';
import { ProfileSyncResponse } from 'app/gamma/src/types/responses';
import { AtlassianAccountOnboardingLinks } from '../AtlassianAccountOnboardingLinks';
// eslint-disable-next-line @trello/less-matches-component
import styles from './AtlassianAccountOnboardingOverlay.less';
import { useAlert, AlertContainer } from 'app/gamma/src/components/alert';
import api from 'app/gamma/src/api';

const ATLASSIAN_PROFILE_SYNC_ALERT_AREA = 'ATLASSIAN_PROFILE_SYNC_ALERT_AREA';
const ATLASSIAN_PROFILE_SYNC_ALERT_NAME = 'ATLASSIAN_PROFILE_SYNC_ALERT_NAME';

const format = forNamespace(['aa onboarding', 'dialog'], {
  shouldEscapeStrings: false,
});
const formatError = forTemplate('app_management');
interface LoginWithAtlassianProps {
  aaEmail: string;
  onClickGoBack: () => void;
  onDismiss: () => void;
  onClickLogin: () => void;
  profileSyncResponseProvider?: () => Promise<ProfileSyncResponse>;
}

/**
 * Extracted to allow testing/storybooking this component.
 * Not expecting this property to be overridden by consumers.
 */
const defaultProfileSyncResponseProvider = () => {
  return api.client.rest.post<ProfileSyncResponse>(
    'members/me/atlassianAccount/setup',
    {
      query: {
        returnUrl: window.location.pathname,
      },
    },
  );
};

export const LoginWithAtlassianV2: FunctionComponent<LoginWithAtlassianProps> = ({
  aaEmail,
  onClickGoBack,
  onDismiss,
  onClickLogin,
  profileSyncResponseProvider = defaultProfileSyncResponseProvider,
}) => {
  const { showAlert: showErrorAlert } = useAlert({
    alertArea: ATLASSIAN_PROFILE_SYNC_ALERT_AREA,
    alertName: ATLASSIAN_PROFILE_SYNC_ALERT_NAME,
  });

  const [requiresEmailVerification, setRequiresEmailVerification] = useState(
    false,
  );

  const atlassianLogin = async () => {
    onClickLogin();
    try {
      const response = await profileSyncResponseProvider();
      if (response.emailVerificationRequired) {
        setRequiresEmailVerification(true);
      } else {
        window.location.assign(response.redirect_url);
      }
    } catch (err) {
      if (err.error_code === 'ALREADY_MASTERED') {
        onDismiss();
        return;
      }
      showErrorAlert();
    }
  };

  if (requiresEmailVerification) {
    return (
      <OnboardingDialog
        title={format('check your inbox')}
        helpLink={AtlassianAccountOnboardingLinks.HelpV2}
        closeDialog={onDismiss}
        className={styles.loginWithAtlassianBody}
      >
        <div className={styles.loginWithAtlassianContent}>
          <p>
            {format('we sent an email', {
              email: <strong key="email">{aaEmail}</strong>,
            })}
          </p>
          <p>{format('click the link in the email')}</p>
        </div>
        <div className={styles.section}>
          <PrimaryButton onClick={onDismiss}>{format('got it')}</PrimaryButton>
        </div>
      </OnboardingDialog>
    );
  }
  return (
    <>
      <AlertContainer
        displayType="error"
        name={ATLASSIAN_PROFILE_SYNC_ALERT_NAME}
        area={ATLASSIAN_PROFILE_SYNC_ALERT_AREA}
      >
        {formatError('something-went-wrong')}
      </AlertContainer>
      <OnboardingDialog
        onClickGoBack={onClickGoBack}
        title={format('connect to atlassian')}
        helpLink={AtlassianAccountOnboardingLinks.HelpV2}
        closeDialog={onDismiss}
        className={styles.loginWithAtlassianBody}
      >
        <div className={styles.loginWithAtlassianContent}>
          <p>{format('connect explaination')}</p>
          <p>{format('connect msg')}</p>
        </div>
        <div className={styles.section}>
          <PrimaryButton onClick={atlassianLogin}>
            {format('continue')}
          </PrimaryButton>
        </div>
      </OnboardingDialog>
    </>
  );
};
